import { Modal } from '@kluein/klue-ui';
import { Box, Image } from '@mantine/core';
import { useState } from 'react';

import {
  AnalyticsAction,
  AnalyticsCategory,
  useAnalytics,
} from 'lib/analytics';
import { KLUE_CARD_NO_ZOOM_IMG } from 'lib/constants';
import TEST_IDS from 'test-ids';

import ImageOverlay from './ImageOverlay';

import { useCardInteractionLog } from '../../hooks';

import type { ImageContentProps } from './ImageContent.types';

export default function ImageContent({
  children,
  src: imageSrc,
  externalLink,
  zoom,
  logEventLabel,
  cardId,
  rank,
  className,
  ...imageProps
}: ImageContentProps) {
  const { logEvent } = useAnalytics();
  const logCardInteraction = useCardInteractionLog();
  const [isHovered, setIsHovered] = useState(false);
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
  const { href: linkHref, target: linkTarget = '_blank' } = externalLink || {};

  const handleZoomImage = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();

    let zoomId = 'img0';
    const target = event.target as HTMLElement;
    const parent = target?.parentElement?.parentElement;
    const img = parent?.querySelector('img') as HTMLImageElement;
    const index = Array.from(
      img
        ?.closest('[data-card-id]')
        ?.querySelectorAll(`img:not(.${KLUE_CARD_NO_ZOOM_IMG}`) || [],
    ).indexOf(img);

    if (index >= 0) {
      zoomId = `img${index}`;
    }

    setIsZoomModalOpen(true);

    logEvent({
      event: {
        category: AnalyticsCategory.zoom,
        action: AnalyticsAction.view,
        label: `${logEventLabel} : ${zoomId}`,
      },
    });

    if (cardId) {
      logCardInteraction({
        action: AnalyticsAction.image,
        label: zoomId,
        cardId,
        rank,
      });
    }

    return false;
  };

  const handleCloseModal = () => {
    setIsZoomModalOpen(false);
  };

  const zoomProps = zoom
    ? {
        tabIndex: 0,
        role: 'button',
        onClick: handleZoomImage,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            return handleZoomImage(event);
          }
        },
      }
    : {};

  const contentZoomId = `contentzoom${cardId || 0}-img${rank || 0}`;

  return (
    <>
      <Box
        data-test-id={TEST_IDS.klueCard.transform.image.container}
        data-image-container
        data-contentzoom-id={contentZoomId}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        pos="relative"
        display="flex"
        style={{ flexDirection: 'column' }}
        {...zoomProps}
      >
        <ImageOverlay
          isHovered={isHovered}
          isZoomed={isZoomModalOpen}
          isFullWidth={
            className?.split(' ').includes('klue-img--fullwidth') || false
          }
          zoom={zoom}
          linkHref={linkHref}
          linkTarget={linkTarget}
        />

        {linkHref ? (
          children
        ) : (
          <Image
            data-test-id={TEST_IDS.klueCard.transform.image.image}
            className={className}
            {...imageProps}
            src={imageSrc}
            loading="lazy"
          />
        )}
      </Box>

      {isZoomModalOpen && (
        <Modal
          onClose={handleCloseModal}
          showCloseButton={false}
          testId="image-zoom-modal"
          className={`klue-image-modal ${
            linkHref ? 'klue-modal-bottom-link' : ''
          }`}
        >
          <Modal.Content pad="none">
            <Image
              src={imageSrc}
              loading="lazy"
              style={{
                maxHeight: 'unset',
                overflow: 'visible',
              }}
            />
          </Modal.Content>
        </Modal>
      )}
    </>
  );
}
